import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, RefreshCwIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { detailQuestions, requests, statusLabels } from '../data/demandes';
import { StatusProgress } from '../components/demandes/RequestList';
import { RequestDetailsPanel } from '../components/demandes/RequestDetailsPanel';
import { RequestMessages } from '../components/demandes/RequestMessages';
const answers: Record<string, string> = {
  'Where does my request stand?': 'Your request has been with Public Works since August 28.',
  'When will it be handled?': 'No completion date is available yet. You will be notified as soon as the status changes.',
  'Is there anything else I need to do?': 'No, nothing is needed from you right now.'
};
type Tab = 'details' | 'messages';
export function DemandeDetail() {
  const {
    id
  } = useParams();
  const request = requests.find(item => item.id === id) ?? requests[0];
  const [tab, setTab] = useState<Tab>('details');
  const [asked, setAsked] = useState<string | null>(null);
  const pending = request.messages.filter(message => message.author === 'ville' && message.awaiting).length;
  const tabs: {
    id: Tab;
    label: string;
    badge?: number;
  }[] = [{
    id: 'details',
    label: 'Details'
  }, {
    id: 'messages',
    label: 'Messages',
    badge: pending || undefined
  }];
  return <main className="bg-white" data-id="element-1930" data-mp="364dhkon6s294">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-1931" data-mp="316tv51gc4j63">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-1932" data-mp="315io8srlevar">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-1933" data-mp="2r97adycctxe7">
            Home
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1934" data-mp="1ktdf3cqp9rzi" />
          <Link to="/my-requests" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-1935" data-mp="1fu0k24podelu">
            My requests
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1936" data-mp="7vmq178vivg7" />
          <span aria-current="page" data-id="element-1937" data-mp="bvwtac5a77rd">{request.reference}</span>
        </nav>

        <header className="mt-4" data-id="element-1938" data-mp="btu4fvfv7ko8">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1939" data-mp="3u2kxlzsibqvz">
            <div data-id="element-1940" data-mp="2vi95g41zypg">
              <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-1941" data-mp="2uywadt9g0ia">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1942" data-mp="3kax9o8v5srz9" />
                {request.service}
              </p>
              <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-ville-navy" data-id="element-1943" data-mp="3p3zy62l2xb8l">{request.title}</h1>
              <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-1944" data-mp="3b97bmk6ruusi">
                {request.category} · Request {request.reference}
              </p>
            </div>
            <button type="button" aria-label="Refresh the request" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1945" data-mp="31efetil0kbys">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-1946" data-mp="2wpmm5wco27kp" />
              Refresh
            </button>
          </div>

          <div className="mt-5" data-id="element-1947" data-mp="2ro70q36ktz1s">
            <StatusProgress status={request.status} data-id="element-1948" data-mp="35p9nki5ebddr" />
          </div>
        </header>

        <div role="tablist" aria-label="Request sections" className="mt-8 flex gap-6 border-b border-slate-200" data-id="element-1949" data-mp="35mx0xrop0eud">
          {tabs.map(item => {
          const active = tab === item.id;
          return <button key={item.id} type="button" role="tab" id={`tab-${item.id}`} aria-selected={active} aria-controls={`panel-${item.id}`} onClick={() => setTab(item.id)} className={`relative -mb-px flex items-center gap-2 pb-3 text-[15px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'text-ville-navy' : 'text-slate-500 hover:text-ville-navy'}`} data-id="element-1950" data-mp="3fh7669yqh7mi">
                {item.label}
                {item.badge && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-ville-lime px-1.5 text-[11px] font-bold text-ville-navy" data-id="element-1951" data-mp="1moikyjcxpxgf">
                    {item.badge}
                  </span>}
                {active && <motion.span layoutId="request-tab" transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-ville-navy" aria-hidden="true" data-id="element-1952" data-mp="1wi94ray49jps" />}
              </button>;
        })}
        </div>

        {tab === 'details' ? <div role="tabpanel" id="panel-details" aria-labelledby="tab-details" className="mt-6" data-id="element-1953" data-mp="1dpx36qfaemgp">
            <RequestDetailsPanel request={request} data-id="element-1954" data-mp="2k5zdb9gmk798" />

            <p className="mt-4 text-xs text-slate-500" data-id="element-1955" data-mp="2k5hatv3dcwsv">Current status: {statusLabels[request.status]}</p>

            <section aria-labelledby="atna-request-title" className="mt-6 rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-1956" data-mp="2p391ymw6wew0">
              <h2 id="atna-request-title" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-1957" data-mp="djjlojce5tbn">
                <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-1958" data-mp="3rnl91mu1r49" />
                Ask ATNA
              </h2>

              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1959" data-mp="3uxfalyssczq0">
                {detailQuestions.map(question => <li key={question} data-id="element-1960" data-mp="3q06ppqg96hl2">
                    <button type="button" onClick={() => setAsked(question)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${asked === question ? 'bg-ville-indigo text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-1961" data-mp="3px12yhv3jrsi">
                      {question}
                    </button>
                  </li>)}
              </ul>

              {asked && <p className="mt-4 rounded-2xl bg-ville-indigo-soft px-5 py-4 text-sm leading-relaxed text-ville-navy" data-id="element-1962" data-mp="1a66j055mxl0a">
                  {answers[asked]}
                </p>}

              <p className="mt-4 text-xs text-slate-500" data-id="element-1963" data-mp="39g92p3m9mkjn">
                Updates to this request also appear under{' '}
                <Link to="/notifications" className="font-bold text-ville-indigo hover:underline" data-id="element-1964" data-mp="2uo78o06vmlrj">
                  Notifications
                </Link>
                .
              </p>
            </section>
          </div> : <div role="tabpanel" id="panel-messages" aria-labelledby="tab-messages" className="mt-6" data-id="element-1965" data-mp="3sdim4krlby0w">
            <RequestMessages request={request} data-id="element-1966" data-mp="3dgrvp0zw9wnf" />
          </div>}
      </div>
    </main>;
}